import React, { useState, useEffect } from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Grid,
  TextField,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Divider,
  Alert,
  Tooltip,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
  Badge
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Calculate as CalculateIcon,
  LocalOffer as QuoteIcon,
  EventBusy as CancelIcon,
  CheckCircle as CompleteIcon,
  Build as ServiceIcon,
  Receipt as InvoiceIcon,
  Description as ReportIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { supabase } from '../lib/supabase';
import { generateInvoiceNumber, generateJobNumber } from '../services/invoice.service';

interface ServicePart {
  id?: string;
  part_number: string;
  description: string;
  quantity: number;
  unit_price: number;
  total: number;
}

interface ServiceFlowData {
  // Step 1: Customer & Vehicle
  customer_id: string;
  vehicle_id: string;
  
  // Step 2: Service Type
  service_type: string;
  is_quote: boolean;
  quote_expiry?: string;
  
  // Step 3: Service Details
  service_description: string;
  estimated_duration: string;
  estimated_cost: number;
  priority: string;
  technician?: string;
  
  // Step 4: Parts (if any)
  parts: ServicePart[];
  
  // Additional Info
  notes?: string;
  customer_requests?: string;
}

interface ServiceFlowDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editMode?: boolean;
  initialData?: any;
}

const SERVICE_TYPES = [
  'Full Service',
  'Oil Change',
  'Brake Service',
  'Tire Service',
  'Battery Replacement',
  'Transmission Service',
  'Engine Diagnostics',
  'Air Conditioning',
  'Electrical Repair',
  'Suspension',
  'Exhaust',
  'Inspection',
  'Other'
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low', color: 'success' },
  { value: 'normal', label: 'Normal', color: 'info' },
  { value: 'high', label: 'High', color: 'warning' },
  { value: 'urgent', label: 'Urgent', color: 'error' }
];

export default function ServiceFlowDialog({ 
  open, 
  onClose, 
  onSuccess, 
  editMode = false, 
  initialData 
}: ServiceFlowDialogProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeStep, setActiveStep] = useState(0);
  const [mode, setMode] = useState<'quote' | 'booking'>('booking');
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState<any[]>([]);
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [technicians, setTechnicians] = useState<any[]>([]);
  const [serviceProviders, setServiceProviders] = useState<any[]>([]);
  
  const [data, setData] = useState<ServiceFlowData>({
    customer_id: '',
    vehicle_id: '',
    service_type: '',
    is_quote: false,
    service_description: '',
    estimated_duration: '01:00:00',
    estimated_cost: 0,
    priority: 'normal',
    parts: []
  });

  const steps = [
    'Customer & Vehicle',
    'Service Type',
    'Service Details',
    'Parts & Materials',
    'Review & Confirm'
  ];

  useEffect(() => {
    if (open) {
      loadInitialData();
      if (editMode && initialData) {
        // Load existing data for editing
        setData({
          ...data,
          ...initialData,
          parts: initialData.parts || []
        });
        setMode(initialData.is_quote ? 'quote' : 'booking');
      }
    }
  }, [open, editMode, initialData]);

  const loadInitialData = async () => {
    try {
      // Load customers - only active ones with vehicle count
      const { data: customersData } = await supabase
        .from('customers')
        .select('id, first_name, last_name, phone, email, vehicles(count)')
        .eq('is_active', true)
        .order('last_name');

      // Sort customers: those with vehicles first, then by last name
      const sortedCustomers = (customersData || []).sort((a, b) => {
        const aVehicleCount = a.vehicles?.[0]?.count || 0;
        const bVehicleCount = b.vehicles?.[0]?.count || 0;

        // If one has vehicles and the other doesn't, prioritize the one with vehicles
        if (aVehicleCount > 0 && bVehicleCount === 0) return -1;
        if (aVehicleCount === 0 && bVehicleCount > 0) return 1;

        // If both have vehicles or both don't, sort by last name
        return a.last_name.localeCompare(b.last_name);
      });

      // Load service providers (technicians)
      const { data: providersData } = await supabase
        .from('service_providers')
        .select('*')
        .eq('active', true);
      
      // Load users with technician role
      const { data: usersData } = await supabase
        .from('users')
        .select(`
          id,
          email,
          profile:profiles(first_name, last_name)
        `)
        .eq('role', 'technician');

      setCustomers(sortedCustomers);
      setServiceProviders(providersData || []);
      setTechnicians(usersData?.map((u: any) => ({
        id: u.id,
        name: `${u.profile?.first_name} ${u.profile?.last_name}`.trim() || u.email
      })) || []);
    } catch (error) {
      console.error('Error loading initial data:', error);
    }
  };

  useEffect(() => {
    if (data.customer_id) {
      loadCustomerVehicles(data.customer_id);
    }
  }, [data.customer_id]);

  const loadCustomerVehicles = async (customerId: string) => {
    try {
      const { data: vehiclesData } = await supabase
        .from('vehicles')
        .select('*')
        .eq('customer_id', customerId)
        .order('year', { ascending: false });
      
      setVehicles(vehiclesData || []);
    } catch (error) {
      console.error('Error loading vehicles:', error);
    }
  };

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const addPart = () => {
    const newPart: ServicePart = {
      part_number: '',
      description: '',
      quantity: 1,
      unit_price: 0,
      total: 0
    };
    setData({
      ...data,
      parts: [...data.parts, newPart]
    });
  };

  const updatePart = (index: number, field: keyof ServicePart, value: any) => {
    const updatedParts = [...data.parts];
    updatedParts[index] = {
      ...updatedParts[index],
      [field]: value
    };
    
    // Recalculate total
    if (field === 'quantity' || field === 'unit_price') {
      updatedParts[index].total = updatedParts[index].quantity * updatedParts[index].unit_price;
    }
    
    setData({
      ...data,
      parts: updatedParts
    });
  };

  const removePart = (index: number) => {
    const updatedParts = data.parts.filter((_, i) => i !== index);
    setData({
      ...data,
      parts: updatedParts
    });
  };

  const calculateTotalCost = () => {
    const partsTotal = data.parts.reduce((sum, part) => sum + part.total, 0);
    return data.estimated_cost + partsTotal;
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const invoiceNumber = !data.is_quote ? await generateInvoiceNumber() : null;
      const jobNumber = await generateJobNumber();
      
      const serviceData = {
        customer_id: data.customer_id,
        vehicle_id: data.vehicle_id,
        appointment_date: format(new Date(), 'yyyy-MM-dd'),
        appointment_time: '09:00:00',
        service_type: data.service_type,
        service_description: data.service_description,
        estimated_cost: calculateTotalCost().toString(),
        estimated_duration: data.estimated_duration,
        priority: data.priority,
        technician: data.technician,
        status: data.is_quote ? 'quote' : 'scheduled',
        invoice_number: invoiceNumber,
        job_number: jobNumber,
        parts_replaced: JSON.stringify(data.parts),
        notes: data.notes,
        is_quote: data.is_quote,
        quote_expiry: data.quote_expiry,
        created_by: supabase.auth.user()?.id
      };

      if (data.is_quote) {
        // Create quote record
        const { error } = await supabase
          .from('service_quotes')
          .insert([serviceData]);
        
        if (error) throw error;
      } else {
        // Create service appointment
        const { error } = await supabase
          .from('vehicle_service_history')
          .insert([serviceData]);
        
        if (error) throw error;
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error creating service:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Autocomplete
                  options={customers}
                  getOptionLabel={(option) => {
                    const vehicleCount = option.vehicles?.[0]?.count || 0;
                    const vehicleText = vehicleCount === 1 ? 'vehicle' : 'vehicles';
                    return `${option.first_name} ${option.last_name} - ${vehicleCount} ${vehicleText}`;
                  }}
                  value={customers.find(c => c.id === data.customer_id) || null}
                  onChange={(event, newValue) => {
                    setData({
                      ...data,
                      customer_id: newValue?.id || '',
                      vehicle_id: ''
                    });
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Select Customer" fullWidth required />
                  )}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Autocomplete
                  options={vehicles}
                  getOptionLabel={(option) =>
                    `${option.year} ${option.make} ${option.model} - ${option.registration}`
                  }
                  value={vehicles?.find(v => v.id === data.vehicle_id) || null}
                  onChange={(event, newValue) => {
                    setData({
                      ...data,
                      vehicle_id: newValue?.id || ''
                    });
                  }}
                  disabled={!data.customer_id}
                  noOptionsText={!data.customer_id ? "Please select a customer first" : "No vehicles found for this customer"}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Vehicle"
                      fullWidth
                      required
                      helperText={!data.customer_id ? "Select a customer to see their vehicles" : vehicles.length === 0 ? "This customer has no vehicles. Add a vehicle first." : ""}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                What type of service is this?
              </Typography>
              <Tabs
                value={mode}
                onChange={(e, newValue) => {
                  setMode(newValue);
                  setData({
                    ...data,
                    is_quote: newValue === 'quote'
                  });
                }}
                centered
              >
                <Tab 
                  icon={<ServiceIcon />} 
                  label="Service Booking" 
                  value="booking"
                />
                <Tab 
                  icon={<QuoteIcon />} 
                  label="Quote Only" 
                  value="quote"
                />
              </Tabs>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Autocomplete
                  options={SERVICE_TYPES}
                  value={data.service_type}
                  onChange={(event, newValue) => {
                    setData({
                      ...data,
                      service_type: newValue || ''
                    });
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Service Type" fullWidth required />
                  )}
                />
              </Grid>

              {mode === 'quote' && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Quote Expiry Date"
                    type="date"
                    value={data.quote_expiry || ''}
                    onChange={(e) => setData({
                      ...data,
                      quote_expiry: e.target.value
                    })}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  label="Service Description"
                  multiline
                  rows={4}
                  value={data.service_description}
                  onChange={(e) => setData({
                    ...data,
                    service_description: e.target.value
                  })}
                  fullWidth
                  required
                  placeholder="Describe what service work is needed..."
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Estimated Duration"
                  type="time"
                  value={data.estimated_duration}
                  onChange={(e) => setData({
                    ...data,
                    estimated_duration: e.target.value
                  })}
                  fullWidth
                  inputProps={{ step: 300 }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Estimated Labor Cost"
                  type="number"
                  value={data.estimated_cost}
                  onChange={(e) => setData({
                    ...data,
                    estimated_cost: parseFloat(e.target.value) || 0
                  })}
                  fullWidth
                  InputProps={{
                    startAdornment: '$'
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={data.priority}
                    label="Priority"
                    onChange={(e) => setData({
                      ...data,
                      priority: e.target.value
                    })}
                  >
                    {PRIORITY_OPTIONS.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              backgroundColor: theme.palette[option.color].main
                            }}
                          />
                          {option.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={technicians}
                  getOptionLabel={(option) => option.name}
                  value={technicians.find(t => t.id === data.technician) || null}
                  onChange={(event, newValue) => {
                    setData({
                      ...data,
                      technician: newValue?.id || ''
                    });
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Assign Technician" fullWidth />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Customer Requests/Notes"
                  multiline
                  rows={3}
                  value={data.customer_requests}
                  onChange={(e) => setData({
                    ...data,
                    customer_requests: e.target.value
                  })}
                  fullWidth
                  placeholder="Any specific requests from the customer?"
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 3:
        return (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Parts & Materials</Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={addPart}
                variant="outlined"
              >
                Add Part
              </Button>
            </Box>

            {data.parts.length > 0 ? (
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Part #</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell align="right">Unit Price</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.parts.map((part, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <TextField
                            size="small"
                            value={part.part_number}
                            onChange={(e) => updatePart(index, 'part_number', e.target.value)}
                            placeholder="Part #"
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={part.description}
                            onChange={(e) => updatePart(index, 'description', e.target.value)}
                            placeholder="Description"
                            fullWidth
                          />
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            type="number"
                            size="small"
                            value={part.quantity}
                            onChange={(e) => updatePart(index, 'quantity', parseInt(e.target.value) || 0)}
                            InputProps={{ sx: { width: 60 } }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            type="number"
                            size="small"
                            value={part.unit_price}
                            onChange={(e) => updatePart(index, 'unit_price', parseFloat(e.target.value) || 0)}
                            InputProps={{ 
                              sx: { width: 80 },
                              startAdornment: '$'
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            ${part.total.toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => removePart(index)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info">No parts added. Click "Add Part" to include parts in this service.</Alert>
            )}

            <Box sx={{ mt: 2 }}>
              <Typography variant="h6">
                Cost Summary
              </Typography>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={6}>
                  <Typography>Labor Cost:</Typography>
                </Grid>
                <Grid item xs={6} textAlign="right">
                  <Typography>${data.estimated_cost.toFixed(2)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography>Parts Total:</Typography>
                </Grid>
                <Grid item xs={6} textAlign="right">
                  <Typography>
                    ${data.parts.reduce((sum, part) => sum + part.total, 0).toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="h6">Total Cost:</Typography>
                </Grid>
                <Grid item xs={6} textAlign="right">
                  <Typography variant="h6">
                    ${calculateTotalCost().toFixed(2)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Box>
        );

      case 4:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Review {mode === 'quote' ? 'Quote' : 'Service Booking'} Details
            </Typography>
            
            <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Customer & Vehicle
                  </Typography>
                  <Typography variant="body1">
                    {customers.find(c => c.id === data.customer_id)?.first_name} {customers.find(c => c.id === data.customer_id)?.last_name}
                  </Typography>
                  <Typography variant="body2">
                    {vehicles?.find(v => v.id === data.vehicle_id)?.year} {vehicles?.find(v => v.id === data.vehicle_id)?.make} {vehicles?.find(v => v.id === data.vehicle_id)?.model}
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Service Details
                  </Typography>
                  <Typography variant="body1">
                    {data.service_type}
                  </Typography>
                  <Typography variant="body2">
                    {data.service_description}
                  </Typography>
                </Grid>
                
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Duration
                  </Typography>
                  <Typography variant="body1">{data.estimated_duration}</Typography>
                </Grid>
                
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Priority
                  </Typography>
                  <Chip 
                    label={data.priority} 
                    size="small"
                    color={PRIORITY_OPTIONS.find(p => p.value === data.priority)?.color as any}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Total Cost
                  </Typography>
                  <Typography variant="h6">
                    ${calculateTotalCost().toFixed(2)}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {mode === 'quote' && (
              <Alert severity="warning">
                This will create a quote that can be converted to a service booking later.
                {data.quote_expiry && ` Quote expires on ${format(new Date(data.quote_expiry), 'dd/MM/yyyy')}.`}
              </Alert>
            )}
          </Box>
        );

      default:
        return 'Unknown step';
    }
  };

  const isStepOptional = (step: number) => {
    return step === 3; // Parts step is optional
  };

  const isStepComplete = (step: number) => {
    // Only mark steps as complete if we've moved past them
    if (step >= activeStep) {
      return false;
    }

    switch (step) {
      case 0:
        return data.customer_id && data.vehicle_id;
      case 1:
        return data.service_type;
      case 2:
        return data.service_description && data.estimated_cost >= 0;
      case 3:
        return true; // Parts are optional, mark complete if we've moved past
      case 4:
        return true; // Review step
      default:
        return false;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: { minHeight: isMobile ? '100vh' : '70vh' } }}
    >
      <DialogTitle>
        <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          {/* QUOTE MODE COMMENTED OUT (REDUNDANT) */}
          {editMode ? 'Edit Service' : /* mode === 'quote' ? 'Create Quote' : */ 'Book Service'}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Stepper
          activeStep={activeStep}
          orientation={isMobile ? 'vertical' : 'horizontal'}
          sx={{ mb: 4 }}
        >
          {steps.map((label, index) => {
            const stepProps: { completed?: boolean } = {};
            const labelProps: { optional?: React.ReactNode } = {};

            if (isStepOptional(index)) {
              labelProps.optional = <Typography variant="caption">Optional</Typography>;
            }

            if (isStepComplete(index)) {
              stepProps.completed = true;
            }

            return (
              <Step key={label} {...stepProps}>
                <StepLabel {...labelProps}>{label}</StepLabel>
              </Step>
            );
          })}
        </Stepper>
        
        {getStepContent(activeStep)}
      </DialogContent>
      
      <DialogActions sx={{ p: { xs: 2, sm: 3 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button
            onClick={activeStep === 0 ? onClose : handleBack}
            fullWidth={isMobile}
            sx={{ order: { xs: 2, sm: 1 } }}
          >
            {activeStep === 0 ? 'Close' : 'Back'}
          </Button>

          <Box sx={{ order: { xs: 1, sm: 2 }, width: { xs: '100%', sm: 'auto' } }}>
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading}
                startIcon={!isMobile && (/* mode === 'quote' ? <QuoteIcon /> : */ <ServiceIcon />)}
                fullWidth={isMobile}
              >
                {/* QUOTE MODE COMMENTED OUT (REDUNDANT) */}
                {loading ? 'Creating...' : /* mode === 'quote' ? 'Create Quote' : */ 'Book Service'}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!isStepComplete(activeStep)}
                fullWidth={isMobile}
              >
                Next
              </Button>
            )}
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
}